import { useLayoutEffect, useRef, useState } from 'react';
import { REACTION_EMOJIS, type ReactionEmoji } from '@sokdaq/protocol';
import { useDismiss } from '../hooks/useDismiss.js';
import { useIsTouch } from '../hooks/useTouch.js';
import { playSound } from '../sound.js';
import { useChatStore } from '../store/chat.js';
import { useSettingsStore } from '../store/settings.js';
import { isGrouped, reactionChips, type DisplayMessage } from '../store/message-list.js';
import { ConnectionBanner } from '../components/ConnectionBanner.js';
import { Composer } from '../components/Composer.js';
import { TypingIndicator } from '../components/TypingIndicator.js';

/**
 * 표준 채팅 뷰.
 *
 * 실시간 연결이 처음 붙는 화면이자, 긴 대화를 읽는 실용 화면.
 */
export function ClassicView() {
  const messages = useChatStore((s) => s.messages);
  const members = useChatStore((s) => s.members);
  const you = useChatStore((s) => s.you);
  const reactions = useChatStore((s) => s.reactions);
  // 훅은 여기서 한 번만 부른다. 행마다 부르면 메시지 수만큼
  // matchMedia 리스너가 달린다.
  const isTouch = useIsTouch();

  const scrollRef = useRef<HTMLDivElement>(null);
  // 위로 올려 예전 대화를 읽는 중이라면 새 메시지가 와도 끌어내리지 않는다.
  const stickToBottom = useRef(true);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (element && stickToBottom.current) {
      element.scrollTop = element.scrollHeight;
    }
  }, [messages]);

  function handleScroll() {
    const element = scrollRef.current;
    if (!element) return;
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    stickToBottom.current = distanceFromBottom < 80;
  }

  const nicknames = new Map(members.map((m) => [m.userId, m.nickname]));

  return (
    <div className="flex h-full flex-col">
      <ConnectionBanner />

      <div ref={scrollRef} onScroll={handleScroll} className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {messages.length === 0 ? (
          <EmptyState />
        ) : (
          <ol className="flex flex-col gap-0.5">
            {messages.map((message, index) => (
              <MessageRow
                key={message.id}
                message={message}
                nickname={nicknames.get(message.userId) ?? '(나간 사람)'}
                isOwn={message.userId === you?.userId}
                groupedWithPrevious={isGrouped(messages[index - 1], message)}
                chips={reactionChips(reactions, message.id, you?.userId)}
                isTouch={isTouch}
              />
            ))}
          </ol>
        )}
      </div>

      <TypingIndicator />
      <Composer />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
      <p className="text-sm text-neutral-400">아직 아무 말도 없습니다</p>
      <p className="text-xs text-neutral-600">먼저 인사를 건네 보세요</p>
    </div>
  );
}

interface Chip {
  emoji: string;
  count: number;
  mine: boolean;
}

function MessageRow({
  message,
  nickname,
  isOwn,
  groupedWithPrevious,
  chips,
  isTouch,
}: {
  message: DisplayMessage;
  nickname: string;
  isOwn: boolean;
  groupedWithPrevious: boolean;
  chips: readonly Chip[];
  isTouch: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  /*
   * 터치에는 호버가 없다.
   *
   * 호버로만 드러나는 버튼은 손가락으로 영영 닿지 않는다 — 반응도,
   * 수정·삭제도 없는 앱이 된다. 그래서 터치에서는 늘 보여준다.
   * 잘못 눌러도 수정은 prompt, 삭제는 confirm을 거치므로 되돌릴 수 있다.
   */
  const showActions = hovered || isTouch;

  if (message.deletedAt !== null) {
    return (
      <li className="px-1 py-0.5 text-sm text-neutral-600 italic">삭제된 메시지</li>
    );
  }

  const isWhisper = message.kind === 'whisper';
  /*
   * 속닥에는 리액션을 달 수 없다. 저장되지 않는 말이라 서버에 붙일 데가
   * 없고(seq 0), 애초에 들은 사람만 아는 말에 공개 반응이 붙으면
   * 못 들은 사람에게 존재가 드러난다.
   */
  const reactable = !isWhisper && message.kind !== 'system' && message.pending !== true;

  return (
    <li
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        /*
         * 터치에서는 피커를 여기서 닫으면 안 된다.
         *
         * 탭하면 크롬이 호환용 마우스 이벤트를 흉내 내는데, click 직후
         * 곧바로 mouseout을 보낸다. 그걸 "마우스가 떠났다"로 읽으면 방금
         * 연 피커가 같은 틱에 닫혀서, 손가락으로는 이모지를 고를 수 없다.
         * 터치의 닫는 길은 useDismiss(바깥 탭·Esc)가 맡는다.
         */
        if (!isTouch) setPickerOpen(false);
      }}
      className={`group relative rounded px-1 ${groupedWithPrevious ? 'py-0' : 'mt-2 py-0.5'} ${
        isWhisper ? 'border-l-2 border-violet-500/60 bg-violet-500/5 pl-2' : ''
      }`}
    >
      {!groupedWithPrevious && (
        <div className="flex items-baseline gap-2">
          <span className={`text-sm font-medium ${isOwn ? 'text-sky-300' : 'text-neutral-200'}`}>
            {nickname}
          </span>
          {isWhisper && <span className="text-[10px] text-violet-400">속닥</span>}
          <time className="text-[10px] text-neutral-600">{formatTime(message.createdAt)}</time>
        </div>
      )}

      <div className="flex items-start gap-2">
        <p
          className={`min-w-0 flex-1 whitespace-pre-wrap break-words text-sm ${
            message.pending ? 'text-neutral-500' : 'text-neutral-100'
          }`}
        >
          {message.body}
          {message.editedAt !== null && (
            <span className="ml-1 text-[10px] text-neutral-600">(수정됨)</span>
          )}
        </p>

        <div className="flex shrink-0 items-start gap-1">
          {reactable && (showActions || pickerOpen) && (
            <button
              type="button"
              onClick={() => setPickerOpen((open) => !open)}
              data-react-toggle
              aria-expanded={pickerOpen}
              aria-label="반응 달기"
              title="반응 달기"
              className="tap rounded px-1 text-[11px] text-neutral-500 hover:bg-neutral-800 hover:text-neutral-300"
            >
              ☺+
            </button>
          )}
          {isOwn && showActions && !message.pending && (
            <MessageActions id={message.id} body={message.body} isTouch={isTouch} />
          )}
        </div>
      </div>

      {chips.length > 0 && (
        <ReactionChips msgId={message.id} chips={chips} />
      )}

      {pickerOpen && (
        <EmojiPicker msgId={message.id} onDone={() => setPickerOpen(false)} />
      )}
    </li>
  );
}

/**
 * 반응을 누르는 손맛.
 *
 * 서버 확정을 기다렸다 그리기 때문에(낙관적으로 그리지 않는다) 누른 직후
 * 화면이 잠깐 그대로다. 소리라도 나야 눌린 걸 안다.
 */
function useReactionClick(): (msgId: string, emoji: ReactionEmoji) => void {
  const toggleReaction = useChatStore((s) => s.toggleReaction);
  return (msgId, emoji) => {
    toggleReaction(msgId, emoji);
    if (useSettingsStore.getState().sound) playSound('react');
  };
}

/** 이미 달린 반응들. 누르면 내 것만 토글된다. */
function ReactionChips({ msgId, chips }: { msgId: string; chips: readonly Chip[] }) {
  const react = useReactionClick();

  return (
    <div className="mt-0.5 flex flex-wrap gap-1">
      {chips.map((chip) => (
        <button
          key={chip.emoji}
          type="button"
          onClick={() => react(msgId, chip.emoji as ReactionEmoji)}
          aria-pressed={chip.mine}
          className={`reaction-chip flex items-center gap-0.5 rounded-full border ${
            chip.mine
              ? 'border-sky-500/60 bg-sky-500/15 text-sky-200'
              : 'border-neutral-700 bg-neutral-800/60 text-neutral-400 hover:border-neutral-600'
          }`}
        >
          <span>{chip.emoji}</span>
          <span className="tabular-nums">{chip.count}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * 이모지 고르기.
 *
 * 전체 이모지를 열지 않고 여섯 개로 못박아 둔다. 서버도 같은 목록만
 * 받으므로(protocol의 REACTION_EMOJIS) 여기서 넓혀 봐야 거부된다.
 */
function EmojiPicker({ msgId, onDone }: { msgId: string; onDone: () => void }) {
  const react = useReactionClick();
  const ref = useRef<HTMLDivElement>(null);

  // 터치에는 mouseleave가 없다. 이게 없으면 한 번 열린 피커가 영영 남는다.
  // 여는 버튼은 제외해야 한다 — 안 그러면 다시 눌러도 닫히지 않는다.
  useDismiss(ref, true, onDone, { toggleSelector: '[data-react-toggle]' });

  return (
    <div
      ref={ref}
      role="group"
      aria-label="이모지 고르기"
      className="absolute right-1 z-10 mt-0.5 flex gap-0.5 rounded-full border border-neutral-700 bg-neutral-900 px-1.5 py-1 shadow-lg"
    >
      {REACTION_EMOJIS.map((emoji) => (
        <button
          key={emoji}
          type="button"
          onClick={() => {
            react(msgId, emoji);
            onDone();
          }}
          aria-label={emoji}
          className="tap rounded px-1 text-sm hover:bg-neutral-800"
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}

function MessageActions({
  id,
  body,
  isTouch,
}: {
  id: string;
  body: string;
  isTouch: boolean;
}) {
  const editMessage = useChatStore((s) => s.editMessage);
  const deleteMessage = useChatStore((s) => s.deleteMessage);

  // 터치에서는 늘 보이는 버튼이 된다. 흐릿하게 두면 오히려 눌러 보게 되니
  // 크기만 키우고 색은 그대로 둔다 — 눈에 띄되 손이 먼저 가지는 않는 정도.
  const size = isTouch ? 'tap px-1.5 text-xs' : '';

  return (
    <div className="flex shrink-0 gap-1 text-[10px] text-neutral-500">
      <button
        type="button"
        className={`${size} rounded hover:text-neutral-300`}
        onClick={() => {
          const next = window.prompt('메시지 수정', body);
          if (next !== null && next.trim() !== body) editMessage(id, next);
        }}
      >
        수정
      </button>
      <button
        type="button"
        className={`${size} rounded hover:text-red-400`}
        onClick={() => {
          if (window.confirm('이 메시지를 지울까요?')) deleteMessage(id);
        }}
      >
        삭제
      </button>
    </div>
  );
}

const timeFormat = new Intl.DateTimeFormat('ko-KR', { hour: '2-digit', minute: '2-digit' });

function formatTime(epochMs: number): string {
  return timeFormat.format(new Date(epochMs));
}
